import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { AppService } from '@Core/services/proprietary/app.service';
import { EditStatusType, StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import { ProductLineConfigObjectState } from '../productline-config/productline-config-object.state';
import { RemoveProductsFromArticle, SetArticleChanged, UpdateArticle } from './article.actions';
import { ArticleState } from './article.state';
import { CostData } from './contract-object.types';
import { UpdateContract } from './contract.actions';
import { ContractState } from './contract.state';
import { LineState } from './line.state';
import {
    AddProductEntities,
    DeleteProduct,
    RemoveSharedProduct,
    RestoreProduct,
    SelectAvailableProduct,
    SetProductAutoDisplay,
    SyncSharedProducts,
    UpdateProduct,
    UpdateProductTotals
} from './product.actions';

export interface ProductStateModel {
    product: any;
}

@State<ProductStateModel>({
    name: 'productState',
    defaults: {
        product: {}
    }
})
@Injectable()
export class ProductState {
    constructor(private helper: StateHelperService, private store: Store, public app: AppService) { }

    //******* SELECTORS *********

    @Selector()
    static products(state: ProductStateModel) {
        //return all products in store
        let result: any = [];
        for (var i in state.product) {
            result.push(state.product[i]);
        }
        return result;
    }

    @Selector()
    static productEntities(state: ProductStateModel) {
        return state.product;
    }

    //TODO -- Write a productsByFilter???   product ID (null, one, list),   Arcticle_ID (one or null),   Product_Code (null, one, list)

    @Selector()
    static productsByIDs(state: ProductStateModel) {
        //return products based on an array of cost_IDs passed in
        return (costIDs: any[]) => {
            let result: any = [];
            if (costIDs) {
                costIDs.forEach(function (thisID) {
                    if (state.product[thisID]) {
                        result.push(state.product[thisID]);
                    }
                });
            }

            result.sort((a, b) => a.Display_Order - b.Display_Order);

            return result;
        };
    }

    @Selector()
    static productByCode(state: ProductStateModel) {
        //return a single product based on article product list and product code
        return (costIDs: any[], productCode: string) => {
            let result: any = {
                Product_Code: { Value: productCode, Desc: 'New ' + productCode + ' Product' }
            };
            //console.log("costids=" + JSON.stringify(costIDs) + " productCode=" + productCode)
            if (costIDs) {
                costIDs.forEach(function (thisID) {
                    if (state.product[thisID].Product_Code == productCode) {
                        result = state.product[thisID];
                    }
                });
            }
            return result;

            //console.log("[productByCode] article=" + JSON.stringify(articleID) + "  product=" + productCode);
            //loop through product array on article and find product matching the productCode

            //if (state.entities.Article[articleID]) {
            //    //console.log("article found " + articleID)
            //    for (var i in state.entities.Article[articleID].Product) {
            //        let costID = state.entities.Article[articleID].Product[i]
            //        //console.log("costid=" + costID)
            //        if (state.entities.Product[costID] && state.state.product[costID].Product_Code.Value == productCode) {
            //            return state.product[costID];
            //        }
            //    }
            //}
            //return an empty product   ???    actually we should trigger a product add action
            //return { Product_Code: { Value: productCode, Desc: 'New ' + productCode + ' Product' } }   
        };
    }

    @Selector()
    static sharedProduct(state: ProductStateModel) {
        //return products based on an array of cost_IDs passed in
        return (product: any) => {
            let productID: any = null;
            let count = 0;
            if (product) {
                productID = product.Product_ID;
                if (productID) {
                    for (var i in state.product) {
                        if (state.product[i].Product_ID && state.product[i].Product_ID == productID) {
                            count++;
                        }
                    }
                }
            }

            if (count > 1) {
                return true;
            } else {
                return false;
            }
        };
    }

    //******  ACTIONS  ******
    @Action(UpdateProduct)
    updateProduct(sc: StateContext<ProductStateModel>, action: UpdateProduct) {
        const state = sc.getState();
        let calc: boolean = false;
        let forceCalc = action.forceCalc;
        let methodChanged: boolean = false;
        let costData: CostData = {};
        let articleID = action.product.Article_ID;
        let error: boolean = false;

        if (!articleID) {
            // TODO: trigger error?
            console.error('ArticleID Missing');
            return;
        }
        let costID = action.product.Cost_ID;
        if (!costID) {
            // TODO: trigger error?
            console.error('CostID Missing');
            return;
        }
        let sharedProduct: boolean = false;

        let updateProduct: any = { ...action.product }; //need DEEP copy?
        let currentProduct: any = { ...state.product[costID] };
        this.helper.setItemChanged(updateProduct, state.product[costID].Edit_Status);

        if (this.helper.stateValueChanged(updateProduct.Incl_Excl, currentProduct.Incl_Excl)) {
            switch (updateProduct.Incl_Excl) {
                case 'E':
                case 'O':
                    updateProduct.Limit_1 = '';
                    updateProduct.Limit_2 = '';
                    updateProduct.Limit_3 = '';
                    updateProduct.Full_Term_Amount = '0.00';

                    break;

                case 'I':
                    updateProduct.Full_Term_Amount = '0.00';
                    break;
            }
        }

        if (updateProduct.Full_Term_Amount != currentProduct.Full_Term_Amount) {
            calc = true;
        }

        if (updateProduct.Net_Change_Amount != currentProduct.Net_Change_Amount) {
            calc = true;
        }

        if (
            updateProduct.Net_Change_Method != currentProduct.Net_Change_Method &&
            currentProduct.Net_Change_Amount != '0' &&
            currentProduct.Full_Term_Amount != '0'
        ) {
            methodChanged = true;
            calc = true;
        }

        if (calc || forceCalc) {
            //TODO: Add calc logic for Cancel
            //TODO: Add logic for Endorse_NonPrem_Ind - Prevent premium changes / disable fields?
            const currentContract = this.store.selectSnapshot(ContractState.contract);

            if (currentContract.Reporting_Ind == 'N' || currentContract.Revision_Object_Code.toUpperCase() == 'AUDIT') {
                if (currentContract.Revision_Object_Code && currentContract.Revision_Object_Code.toUpperCase() == 'AUDIT') {
                    costData.audit = true;
                } else {
                    costData.audit = false;
                }

                if (currentContract.Revision_Object_Code && currentContract.Revision_Object_Code.toUpperCase() == 'CANCEL') {
                    costData.cancel = true;
                } else {
                    costData.cancel = false;
                }

                costData.effDate = new Date(currentContract.Eff_Date);

                //If OOSE then get cancel date, or exp_date from last_of_set revision (account for Extend Term revisions)
                if (currentContract.Out_Of_Seq_Ind == 'Y') {
                    costData.expDate = new Date(currentContract.Out_Of_Seq_Prorate_Date);
                } else {
                    costData.expDate = new Date(currentContract.Exp_Date);
                }
                costData.revDate = new Date(currentContract.Revision_Eff_Date);
                costData.prevFullTerm = Number(currentProduct.Prev_Full_Term_Amount);
                costData.prevWritten = Number(currentProduct.Prev_Written_Amount);
                costData.currNetChange = Number(currentProduct.Net_Change_Amount);
                costData.newNetChange = Number(updateProduct.Net_Change_Amount);
                costData.newFullTerm = Number(updateProduct.Full_Term_Amount);
                costData.netChangeMethod = updateProduct.Net_Change_Method;

                let newCostData: CostData = {};

                if (
                    (updateProduct.Net_Change_Amount != currentProduct.Net_Change_Amount &&
                        currentProduct.Net_Change_Method == 'M') ||
                    (methodChanged && updateProduct.Net_Change_Method == 'M' && updateProduct.Net_Change_Amount != '0')
                ) {
                    let updateFullOnManualNet = this.app.getBookOptionValue('Update_Full_Term_On_Manual_Net_Chg') || 'N';
                    let recalcFullTerm = false;
                    let roundFullTerm = false;
                    switch (updateFullOnManualNet.toUpperCase()) {
                        case 'N': //Never
                            recalcFullTerm = false;
                            break;

                        case 'A': //Always
                        case 'P': //Prompt -- Currently doesn't prompt - SDK will overwrite by auto answering "Yes"
                            recalcFullTerm = true;
                            roundFullTerm = false; //No Rounding
                            break;

                        case 'W': //Always Round to whole dollars
                        case 'R': //Prompt to Round to whole dollars. - Currently doesn't prompt - SDK will overwrite by auto answering "Yes"
                            recalcFullTerm = true;
                            roundFullTerm = true;
                            break;
                    }

                    let article = this.store.selectSnapshot(ArticleState.articleByID)(articleID);
                    if (article && article.Line_ID) {
                        let lineCode = this.store.selectSnapshot(LineState.lineByLineID)(article.Line_ID).Line_Code;
                        if (lineCode) {
                            let config = this.store.selectSnapshot(ProductLineConfigObjectState.productConfig)(
                                lineCode,
                                currentProduct.Product_Code
                            );
                            if (config) {
                                costData.standardValue = config.Standard_Value;
                            }
                        }
                    }

                    newCostData = this.calcFullTermFromManualNet(costData);
                    if (newCostData) {
                        updateProduct.Written_Amount = newCostData.newWritten.toString();
                        if (recalcFullTerm) {
                            if (roundFullTerm) {
                                updateProduct.Full_Term_Amount = Math.round(newCostData.newFullTerm).toString();
                            } else {
                                updateProduct.Full_Term_Amount = newCostData.newFullTerm.toString();
                            }
                        }
                    }
                }

                if ((methodChanged && updateProduct.Net_Change_Method != 'M') || currentProduct.Net_Change_Method != 'M') {
                    if (costData.cancel) {
                        let article = this.store.selectSnapshot(ArticleState.articleByID)(articleID);
                        if (article && article.Line_ID) {
                            let lineCode = this.store.selectSnapshot(LineState.lineByLineID)(article.Line_ID).Line_Code;
                            if (lineCode) {
                                let config = this.store.selectSnapshot(ProductLineConfigObjectState.productConfig)(
                                    lineCode,
                                    currentProduct.Product_Code
                                );
                                if (config) {
                                    costData.standardValue = config.Standard_Value;
                                }
                            }
                        }
                        newCostData = this.calcFullTermFromManualNet(costData);
                    } else {
                        newCostData = this.calculateNet(costData);
                    }
                    if (newCostData) {
                        updateProduct.Written_Amount = newCostData.newWritten.toString();
                        updateProduct.Net_Change_Amount = newCostData.newNetChange.toString();
                    }
                }
            } else {
                if (currentContract.Revision_Object_Code && currentContract.Revision_Object_Code.toUpperCase() == 'SELFAUDIT') {
                    //Ok to set Net_Change_Amount for Reporting Contract if dealing with SelfAudit procedure.
                } else {
                    updateProduct.Net_Change_Amount = '0';
                    //Sure MGA Calculations don't care about setting Unbilled Amount, this is handled by the SDK.
                }
            }
        }

        sc.setState({
            product: {
                ...state.product,
                [costID]: { ...state.product[costID], ...updateProduct }
            }
        });

        sc.dispatch([new SetArticleChanged(articleID), new UpdateProductTotals()]);

        for (var i in state.product) {
            if (state.product[i].Cost_ID != costID && state.product[i].Product_ID == state.product[costID].Product_ID) {
                sharedProduct = true;
                break;
            }
        }

        if (sharedProduct) {
            let syncProductFields: any = [];
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Limit_1')) {
                syncProductFields.push('Limit_1');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Limit_2')) {
                syncProductFields.push('Limit_2');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Limit_3')) {
                syncProductFields.push('Limit_3');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Ded_Type_1')) {
                syncProductFields.push('Ded_Type_1');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Ded_1')) {
                syncProductFields.push('Ded_1');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Ded_Type_2')) {
                syncProductFields.push('Ded_Type_2');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Ded_2')) {
                syncProductFields.push('Ded_2');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Ded_Type_3')) {
                syncProductFields.push('Ded_Type_3');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Ded_3')) {
                syncProductFields.push('Ded_3');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Rate_1')) {
                syncProductFields.push('Rate_1');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Rate_2')) {
                syncProductFields.push('Rate_2');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Rate_3')) {
                syncProductFields.push('Rate_3');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Rate_4')) {
                syncProductFields.push('Rate_4');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Option_1')) {
                syncProductFields.push('Option_1');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Option_2')) {
                syncProductFields.push('Option_2');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Option_3')) {
                syncProductFields.push('Option_3');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Option_4')) {
                syncProductFields.push('Option_4');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Incl_Excl')) {
                syncProductFields.push('Incl_Excl');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Statutory_Limit_Ind')) {
                syncProductFields.push('Statutory_Limit_Ind');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Rejected_Ind')) {
                syncProductFields.push('Rejected_Ind');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Coins_Pct')) {
                syncProductFields.push('Coins_Pct');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Limit_Desc')) {
                syncProductFields.push('Limit_Desc');
            }
            if (this.helper.propertyChanged(currentProduct, updateProduct, 'Retro_Date')) {
                syncProductFields.push('Retro_Date');
            }

            if (syncProductFields.length > 0) {
                sc.dispatch(new SyncSharedProducts(costID, syncProductFields));
            }
        }
    }

    calculateNet(costData: CostData): CostData {
        let netFullTerm;
        let daysInterm;
        let daysleft;
        let factor;
        let annualExp;
        let annualDays;
        var calcFullTerm;

        if (!costData.netChangeMethod) {
            costData.netChangeMethod = 'P';
        }
        if (!costData.prevFullTerm) {
            costData.prevFullTerm = 0;
        }
        if (!costData.prevWritten) {
            costData.prevWritten = 0;
        }
        if (!costData.currNetChange) {
            costData.currNetChange = 0;
        }
        if (!costData.newFullTerm) {
            costData.newFullTerm = 0;
        }
        if (!costData.newNetChange) {
            costData.newNetChange = 0;
        }

        // Initialize return args
        costData.newWritten = 0;

        if (costData.netChangeMethod != 'M') {
            costData.newNetChange = 0;
        }

        //TODO: Book Option for Including Feb 29th in calc.

        if (!costData.audit) {
            // Calculate the net difference in full term
            netFullTerm = costData.newFullTerm - costData.prevFullTerm;

            switch (costData.netChangeMethod) {
                case 'M':
                    // Manual net change entry - just pass back new netchange
                    break;
                case 'F':
                    costData.newNetChange = netFullTerm;
                    break;
                case 'P':
                    if (costData.effDate == costData.expDate) {
                        daysInterm = 1;
                    } else {
                        daysInterm = this.daysBetween(costData.expDate, costData.effDate);
                    }
                    if (costData.revDate == costData.expDate) {
                        daysleft = 1;
                    } else {
                        daysleft = this.daysBetween(costData.expDate, costData.revDate);
                    }

                    let tempDateString = costData.effDate.toString();
                    let tempDate = new Date(tempDateString);

                    annualExp = new Date(tempDate.setMonth(tempDate.getMonth() + 12));
                    annualDays = this.daysBetween(annualExp, costData.effDate);

                    // On a prorated contract, calculate the netchange as a full term contract (required for accurate rounding)
                    if (daysInterm != daysleft) {
                        // Calculate annual prorate factor
                        //factor = Number(Number(daysInterm / annualDays).toFixed(3));
                        factor = Number((daysInterm / annualDays).toFixed(3));
                        calcFullTerm = netFullTerm / factor;

                        // Calculate days left prorate factor
                        factor = Number(Number(daysleft / annualDays).toFixed(3));

                        //TODO: Use Book Option Round_net_prem_use_code to determine Round Precision
                        // New netchange is calculated full term * factor
                        costData.newNetChange = Number(Number(calcFullTerm * factor).toFixed(2));
                    } else {
                        // Calculate prorate factor
                        factor = Number(Number(daysleft / daysInterm).toFixed(3));

                        // Calculate net change only if needed
                        if (costData.newFullTerm != costData.prevFullTerm) {
                            costData.newNetChange = Number(Number(netFullTerm * factor).toFixed(2));
                        }
                    }

                    if (Math.abs(costData.newNetChange) > Math.abs(netFullTerm)) {
                        // make sure we never overcalc
                        costData.newNetChange = netFullTerm;
                    }

                    break;
            }
        } else {
            costData.newNetChange = costData.newFullTerm - costData.prevWritten;
        }

        // New written is the previous revision written + netchange of this revision
        costData.newWritten = costData.prevWritten + costData.newNetChange;

        // Don't worry about unbilled since this calcuation is only for display.

        return costData;
    }

    calcFullTermFromManualNet(costData: CostData): CostData {
        let daysInterm;
        let daysleft;
        let factor;

        if (!costData.newNetChange) {
            costData.newNetChange = 0;
        }

        if (!costData.cancel) {
            if (!costData.audit) {
                if (costData.newNetChange != 0) {
                    if (costData.effDate == costData.expDate) {
                        daysInterm = 1;
                    } else {
                        daysInterm = this.daysBetween(costData.expDate, costData.effDate);
                    }
                    if (costData.revDate == costData.expDate) {
                        daysleft = 1;
                    } else {
                        daysleft = this.daysBetween(costData.expDate, costData.revDate);
                    }
                    if (daysleft == daysInterm) {
                        // Not prorated - add net change to previous full-term to get new full-term
                        costData.newFullTerm = costData.prevFullTerm + costData.newNetChange;
                    } else {
                        // Calculate prorate factor
                        factor = Number((daysleft / daysInterm).toFixed(3));
                        let newFullTerm = Number(((costData.newNetChange + costData.prevFullTerm * factor) / factor).toFixed(2));

                        switch (costData.standardValue) {
                            case 'P':
                                costData.newFullTerm = Math.max(newFullTerm, 0.0);
                                break;

                            case 'N':
                                costData.newFullTerm = Math.min(newFullTerm, 0.0);
                                break;

                            case 'E':
                                costData.newFullTerm = newFullTerm;
                                break;

                            default:
                                costData.newFullTerm = Math.max(newFullTerm, 0.0);
                                break;
                        }
                    }
                } else {
                    // If net change is zero, set new full_term = full_term from prior revision
                    costData.newFullTerm = costData.prevFullTerm;
                }
            } else {
                // Audits - new fullterm = previous written + new manual net change
                costData.newFullTerm = Math.max(costData.prevWritten + costData.newNetChange, 0.0);
            }
        }

        let newWritten = costData.prevWritten + costData.newNetChange;
        switch (costData.standardValue) {
            case 'P':
                costData.newWritten = Math.max(newWritten, 0.0);
                break;

            case 'N':
                costData.newWritten = Math.min(newWritten, 0.0);
                break;

            case 'E':
                costData.newWritten = newWritten;
                break;

            default:
                costData.newWritten = Math.max(newWritten, 0.0);
                break;
        }

        return costData;
    }

    daysBetween(startDate: Date, endDate: Date): number {
        const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
        let diffDays = Math.round(Math.abs((endDate.getTime() - startDate.getTime()) / oneDay));

        return diffDays;
    }

    @Action(DeleteProduct)
    deleteProduct(sc: StateContext<ProductStateModel>, action: DeleteProduct) {
        const state = sc.getState();
        let costID = action.costID;
        let product = { ...state.product };
        let articleID = product[costID].Article_ID;

        this.helper.setItemDeleted(product, costID);
        if (costID > 0) {
            let updateProduct = cloneDeep(product[costID]);
            updateProduct.Full_Term_Amount = 0;
            product = { ...product, [costID]: updateProduct };
        }

        sc.patchState({ product: product });

        if (action.costID < 0) {
            sc.dispatch([new RemoveProductsFromArticle(articleID, [costID])]);
        } else {
            sc.dispatch([new RemoveProductsFromArticle(articleID, [])]); //pass empty array to NOT delete cost record -- will force update of article
        }
    }

    @Action(RestoreProduct)
    restoreProduct(sc: StateContext<ProductStateModel>, action: RestoreProduct) {
        const state = sc.getState();
        let costID = action.costID;
        let product = { ...state.product };
        let articleID = product[costID].Article_ID;

        this.helper.setItemRestored(product, costID);

        sc.patchState({ product: product });

        sc.dispatch([new SetArticleChanged(articleID), new UpdateProductTotals()]);
    }

    @Action(UpdateProductTotals)
    updateProductTotals(sc: StateContext<ProductStateModel>, action: UpdateProductTotals) {
        const state = sc.getState();

        let fullTermPrem: number = 0.0;
        let netChangePrem: number = 0.0;
        let writtenPrem: number = 0.0;
        let fullTermFee: number = 0.0;
        let netChangeFee: number = 0.0;
        let writtenFee: number = 0.0;
        let fullTermTax: number = 0.0;
        let netChangeTax: number = 0.0;
        let writtenTax: number = 0.0;

        for (var i in state.product) {
            let p = state.product[i];

            if (!p.Edit_Status || (p.Edit_Status != 'D' && p.Edit_Status != 'X' && p.Edit_Status != '?')) {
                if (p.Product_Cost_Type) {
                }
                switch (p.Product_Cost_Type) {
                    case 'P':
                        if (p.Full_Term_Amount) {
                            fullTermPrem = fullTermPrem + parseFloat(p.Full_Term_Amount);
                        }
                        if (p.Net_Change_Amount) {
                            netChangePrem = netChangePrem + parseFloat(p.Net_Change_Amount);
                        }
                        if (p.Written_Amount) {
                            writtenPrem = writtenPrem + parseFloat(p.Written_Amount);
                        }
                        //TODO add billed and unbilled
                        break;
                    case 'F':
                        if (p.Full_Term_Amount) {
                            fullTermFee = fullTermFee + parseFloat(p.Full_Term_Amount);
                        }
                        if (p.Net_Change_Amount) {
                            netChangeFee = netChangeFee + parseFloat(p.Net_Change_Amount);
                        }
                        if (p.Written_Amount) {
                            writtenFee = writtenFee + parseFloat(p.Written_Amount);
                        }
                        //TODO add billed and unbilled
                        break;
                    case 'T':
                        if (p.Full_Term_Amount) {
                            fullTermTax = fullTermTax + parseFloat(p.Full_Term_Amount);
                        }
                        if (p.Net_Change_Amount) {
                            netChangeTax = netChangeTax + parseFloat(p.Net_Change_Amount);
                        }
                        if (p.Written_Amount) {
                            writtenTax = writtenTax + parseFloat(p.Written_Amount);
                        }
                        //TODO add billed and unbilled
                        break;
                }
            }
        }

        let contract = {
            Full_Term_Prem: fullTermPrem.toString(),
            Net_Change_Prem: netChangePrem.toString(),
            Written_Prem: writtenPrem.toString(),
            //TODO:  this is a temporary HACK for CCF demo.   Need to properly calculate unbilled.   Need to get unbilled from products!!!!
            Unbilled_Prem: netChangePrem.toString(),
            Full_Term_Tax: fullTermTax.toString(),
            Net_Change_Tax: netChangeTax.toString(),
            Written_Tax: writtenTax.toString(),
            //TODO:  this is a temporary HACK for CCF demo.   Need to properly calculate unbilled.   Need to get unbilled from products!!!!
            Unbilled_Tax: netChangeTax.toString(),
            Full_Term_Fee: fullTermFee.toString(),
            Net_Change_Fee: netChangeFee.toString(),
            Written_Fee: writtenFee.toString(),
            //TODO:  this is a temporary HACK for CCF demo.   Need to properly calculate unbilled.   Need to get unbilled from products!!!!
            Unbilled_Fee: netChangeFee.toString()
        };

        sc.dispatch(new UpdateContract(contract));
    }

    @Action(AddProductEntities) //possibly rename PatchProduct or PatchProducts  or Patch
    AddProductEntities(sc: StateContext<ProductStateModel>, action: AddProductEntities) {
        const state = sc.getState();
        sc.patchState({
            product: { ...state.product, ...action.productEntities }
        });

        if (action.forceCalc) {
            let product: any = {};
            for (var i in action.productEntities) {
                product = action.productEntities[i];
                this.store.dispatch(new UpdateProduct(product, true));
            }
        }
    }

    @Action(SelectAvailableProduct)
    selectAvailableProduct(sc: StateContext<ProductStateModel>, action: SelectAvailableProduct) {
        const state = sc.getState();
        let costID = action.costID;
        let updateProduct = cloneDeep(state.product[costID]);
        let articleID = updateProduct.Article_ID;

        if (updateProduct.Edit_Status == EditStatusType.Available) {
            updateProduct.Edit_Status = EditStatusType.Add;

            sc.setState({
                product: {
                    ...state.product,
                    [costID]: { ...state.product[costID], ...updateProduct }
                }
            });

            sc.dispatch([new SetArticleChanged(articleID)]);
        }
    }

    @Action(SetProductAutoDisplay)
    SetProductAutoDisplay(sc: StateContext<ProductStateModel>, action: SetProductAutoDisplay) {
        const state = sc.getState();
        let displayData = { ...action.autoDisplayData };
        let updateProduct = cloneDeep(action.product);
        let costID = updateProduct.Cost_ID;

        // Specifically doesn't change the Edit Status or trigger an update to the Parent Article record.
        // This action is only to set the temp flags for Default_Ind, Mandatory_Ind and Optional_Ind.
        if (displayData && displayData.Default_Ind) {
            updateProduct.Default_Ind = displayData.Default_Ind;
        }
        if (displayData && displayData.Mandatory_Ind) {
            updateProduct.Mandatory_Ind = displayData.Mandatory_Ind;
        }
        if (displayData && displayData.Optional_Ind) {
            updateProduct.Optional_Ind = displayData.Optional_Ind;
        }

        sc.setState({
            product: {
                ...state.product,
                [costID]: { ...state.product[costID], ...updateProduct }
            }
        });
    }

    @Action(RemoveSharedProduct)
    removeSharedProduct(sc: StateContext<ProductStateModel>, action: RemoveSharedProduct) {
        const state = sc.getState();
        let costID = action.costID;
        let updateProduct = cloneDeep(state.product[costID]);
        let articleID = updateProduct.Article_ID;

        this.helper.setItemChanged(updateProduct, state.product[costID].Edit_Status);

        // Replace Product_ID with a new temp negavtive ID. Will trigger a new product record in SDK.
        // Also remove Product_GUID.
        updateProduct.Product_ID = this.helper.getTempID();
        updateProduct.Product_GUID = '';

        sc.setState({
            product: {
                ...state.product,
                [costID]: { ...state.product[costID], ...updateProduct }
            }
        });

        sc.dispatch([new UpdateArticle({ Article_ID: articleID })]);
    }

    @Action(SyncSharedProducts)
    syncSharedProducts(sc: StateContext<ProductStateModel>, action: SyncSharedProducts) {
        const state = sc.getState();
        let srcProduct = cloneDeep(state.product[action.costID]);
        let updateProductEntities = cloneDeep(state.product);
        let productData: any = {};
        let updateState = false;

        //Build list of updated Product data.
        for (var f in action.modifiedFields) {
            if (srcProduct.hasOwnProperty(action.modifiedFields[f])) {
                productData[action.modifiedFields[f]] = srcProduct[action.modifiedFields[f]];
            }
        }

        for (var i in updateProductEntities) {
            if (
                updateProductEntities[i].Cost_ID != action.costID &&
                updateProductEntities[i].Product_ID == srcProduct.Product_ID
            ) {
                updateState = true;

                this.helper.setItemChanged(updateProductEntities[i], updateProductEntities[i].Edit_Status);
                updateProductEntities[i] = { ...updateProductEntities[i], ...productData };

                sc.dispatch([new SetArticleChanged(updateProductEntities[i].Article_ID)]);
            }
        }

        if (updateState) {
            sc.setState({
                product: {
                    ...state.product,
                    ...updateProductEntities
                }
            });
        }
    }
}
